import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Platform } from 'react-native';
import { PageContainer, ContentContainer, Text, Button, Card, Theme } from '../../components/ui';
import { signInWithApple, signInWithGoogle } from '../../services/authService';

interface TestResult {
  status: 'pending' | 'running' | 'pass' | 'fail';
  message?: string;
  timestamp?: Date;
}

interface ManualTest {
  id: string;
  title: string;
  description: string;
  platform: 'all' | 'web' | 'native' | 'ios' | 'android';
  category: 'auth' | 'payment' | 'data' | 'ui';
  action: () => Promise<void>;
}

const ManualTestsScreen = () => {
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});

  const updateTestResult = (id: string, result: Partial<TestResult>) => {
    setTestResults((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...result, timestamp: new Date() } as TestResult,
    }));
  };

  const runTest = async (test: ManualTest) => {
    updateTestResult(test.id, { status: 'running', message: undefined });
    try {
      await test.action();
      updateTestResult(test.id, { status: 'pass', message: 'Test completed successfully' });
    } catch (error: any) {
      updateTestResult(test.id, { 
        status: 'fail', 
        message: error?.message || 'Test failed with unknown error' 
      });
    }
  };

  // Define manual tests - ONLY tests that require user interaction
  const manualTests: ManualTest[] = [
    {
      id: 'apple-signin-web',
      title: 'Apple Sign-In (Web)',
      description: 'Click to test Apple OAuth flow. You will need to sign in with your Apple ID.',
      platform: 'web',
      category: 'auth',
      action: async () => {
        const result = await signInWithApple();
        if (!result.success) {
          throw new Error(result.error || 'Apple Sign-In failed');
        }
        // Success - status will update automatically
      },
    },
    {
      id: 'google-signin-web',
      title: 'Google Sign-In (Web)',
      description: 'Click to test Google OAuth flow. You will need to sign in with your Google account.',
      platform: 'web',
      category: 'auth',
      action: async () => {
        const result = await signInWithGoogle();
        if (!result.success) {
          throw new Error(result.error || 'Google Sign-In failed');
        }
        // Success - status will update automatically
      },
    },
    {
      id: 'apple-signin-native',
      title: 'Apple Sign-In (iOS Native)',
      description: 'Click to test native Apple Authentication. You will see the Apple sign-in dialog.',
      platform: 'ios',
      category: 'auth',
      action: async () => {
        const result = await signInWithApple();
        if (!result.success) {
          throw new Error(result.error || 'Apple Sign-In failed');
        }
        // Success - status will update automatically
      },
    },
    {
      id: 'google-signin-native',
      title: 'Google Sign-In (Native)',
      description: 'Click to test native Google Sign-In. You will see the Google account picker.',
      platform: 'native',
      category: 'auth',
      action: async () => {
        const result = await signInWithGoogle();
        if (!result.success) {
          throw new Error(result.error || 'Google Sign-In failed');
        }
        // Success - status will update automatically
      },
    },
  ];

  // Filter tests by platform
  const availableTests = manualTests.filter((test) => {
    if (test.platform === 'all') return true;
    if (test.platform === 'web') return Platform.OS === 'web';
    if (test.platform === 'native') return Platform.OS !== 'web';
    if (test.platform === 'ios') return Platform.OS === 'ios';
    if (test.platform === 'android') return Platform.OS === 'android';
    return false;
  });

  const getStatusColor = (status?: 'pending' | 'running' | 'pass' | 'fail') => {
    switch (status) {
      case 'pass':
        return Theme.colors.success;
      case 'fail':
        return Theme.colors.error;
      case 'running':
        return Theme.colors.warning;
      default:
        return Theme.colors.textSecondary;
    }
  };

  const getStatusIcon = (status?: 'pending' | 'running' | 'pass' | 'fail') => {
    switch (status) {
      case 'pass':
        return '✅';
      case 'fail':
        return '❌';
      case 'running':
        return '⏳';
      default:
        return '⚪️';
    }
  };

  return (
    <PageContainer>
      <ContentContainer>
        <ScrollView style={styles.container}>
          <Text variant="heading1" style={styles.title}>
            Manual OAuth Tests
          </Text>
          <Text variant="body1" color="secondary" style={styles.subtitle}>
            Test OAuth flows before deployment. These require you to actually sign in.
          </Text>

          {/* Summary */}
          <Card style={styles.summaryCard}>
            <Text variant="body1" style={styles.summaryText}>
              📊 {availableTests.length} OAuth provider{availableTests.length !== 1 ? 's' : ''} to test on {Platform.OS}
            </Text>
            <Text variant="body2" color="secondary" style={styles.summaryDetail}>
              {Object.values(testResults).filter((r) => r.status === 'pass').length} passed • 
              {Object.values(testResults).filter((r) => r.status === 'fail').length} failed
            </Text>
          </Card>

          {/* Test Categories */}
          {['auth', 'payment', 'data', 'ui'].map((category) => {
            const categoryTests = availableTests.filter((t) => t.category === category);
            if (categoryTests.length === 0) return null;

            return (
              <View key={category} style={styles.categorySection}>
                <Text variant="heading2" style={styles.categoryTitle}>
                  {category.toUpperCase()}
                </Text>
                
                {categoryTests.map((test) => {
                  const result = testResults[test.id] || { status: 'pending' };
                  
                  return (
                    <Card key={test.id} style={styles.testCard}>
                      <View style={styles.testHeader}>
                        <View style={styles.testTitleRow}>
                          <Text style={styles.testIcon}>
                            {getStatusIcon(result.status)}
                          </Text>
                          <Text variant="body1" style={styles.testTitle}>
                            {test.title}
                          </Text>
                        </View>
                        {result.status !== 'pending' && result.timestamp && (
                          <Text variant="caption" color="secondary">
                            {result.timestamp.toLocaleTimeString()}
                          </Text>
                        )}
                      </View>
                      
                      <Text variant="body2" color="secondary" style={styles.testDescription}>
                        {test.description}
                      </Text>
                      
                      {result.message && (
                        <View
                          style={[
                            styles.resultBadge,
                            { backgroundColor: getStatusColor(result.status) + '20' },
                          ]}
                        >
                          <Text
                            variant="body2"
                            style={[styles.resultText, { color: getStatusColor(result.status) }]}
                          >
                            {result.message}
                          </Text>
                        </View>
                      )}
                      
                      <Button
                        title={
                          result.status === 'running'
                            ? 'Running...'
                            : result.status === 'pass'
                            ? 'Run Again'
                            : 'Run Test'
                        }
                        onPress={() => runTest(test)}
                        variant={result.status === 'pass' ? 'secondary' : 'primary'}
                        disabled={result.status === 'running'}
                        style={styles.runButton}
                      />
                    </Card>
                  );
                })}
              </View>
            );
          })}

          {/* Instructions */}
          <Card style={styles.instructionsCard}>
            <Text variant="heading3" style={styles.instructionsTitle}>
              📝 Pre-Deployment Checklist
            </Text>
            <Text variant="body2" color="secondary" style={styles.instructionsText}>
              These OAuth flows cannot be fully automated. Test them before deploying:{'\n'}
              {'\n'}
              1. Tap "Run Test" for each OAuth provider{'\n'}
              2. Complete the sign-in flow in the popup/redirect{'\n'}
              3. Verify status shows ✅ (pass){'\n'}
              4. Test on both desktop and mobile browsers{'\n'}
              {'\n'}
              ⚠️ All tests must pass before production deployment
            </Text>
          </Card>

          <View style={styles.bottomSpacer} />
        </ScrollView>
      </ContentContainer>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  title: {
    marginBottom: Theme.spacing.sm,
    textAlign: 'center',
  },
  subtitle: {
    marginBottom: Theme.spacing.xl,
    textAlign: 'center',
    paddingHorizontal: Theme.spacing.lg,
  },
  summaryCard: {
    marginBottom: Theme.spacing.lg,
    padding: Theme.spacing.md,
  },
  summaryText: {
    marginBottom: Theme.spacing.xs,
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  summaryDetail: {
    fontSize: 14,
  },
  categorySection: {
    marginBottom: Theme.spacing.xl,
  },
  categoryTitle: {
    marginBottom: Theme.spacing.md,
    paddingHorizontal: Theme.spacing.sm,
    color: Theme.colors.primary,
    fontFamily: Theme.typography.fontFamily.bold,
  },
  testCard: {
    marginBottom: Theme.spacing.md,
    padding: Theme.spacing.md,
  },
  testHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: Theme.spacing.sm,
  },
  testTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  testIcon: {
    fontSize: 20,
    marginRight: Theme.spacing.sm,
  },
  testTitle: {
    flex: 1,
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  testDescription: {
    marginBottom: Theme.spacing.md,
    lineHeight: 20,
  },
  resultBadge: {
    padding: Theme.spacing.sm,
    borderRadius: Theme.borderRadius.sm,
    marginBottom: Theme.spacing.md,
  },
  resultText: {
    fontSize: 14,
    lineHeight: 18,
  },
  runButton: {
    marginTop: Theme.spacing.sm,
  },
  instructionsCard: {
    marginTop: Theme.spacing.xl,
    padding: Theme.spacing.md,
    backgroundColor: Theme.colors.primaryLight,
  },
  instructionsTitle: {
    marginBottom: Theme.spacing.md,
    color: Theme.colors.primary,
  },
  instructionsText: {
    lineHeight: 22,
  },
  bottomSpacer: {
    height: Theme.spacing.xxl,
  },
});

export default ManualTestsScreen;

